import { useQueryClient } from '@tanstack/react-query';

import { useMutation, useQuery } from '@hooks/reactQuery';

import { useAppDispatch } from '@store/store';

import { setSnackbar } from '@slices/common';

import { editErrorCode, getErrorsByFilters } from '@api/errors';

import { extractQueryParams } from '@utils/common';

import { TDefaultDTO } from '@interfaces/common/defaultDTO';

import { ESnackbarTypes } from '@enums/common';

import { ERROR_CODE_EDIT, ERRORS_FILTER_LIST, ERRORS_LIST } from '@constants/queryKeys';
import { responseMessages } from '@constants/responseMessages';

interface IUseManageErrorsParams extends TDefaultDTO {
	closeModals?: () => void;
}

export const useManageErrorCodes = (params?: IUseManageErrorsParams) => {
	const dispatch = useAppDispatch();
	const queryClient = useQueryClient();

	const {
		closeModals,
		page,
		pageSize,
		sortDirection,
		sortFieldName,
		searchValue,
	} = params || {};

	const {
		data,
		isLoading: isLoadingErrorsByFilter,
		isFetching: isFetchingErrorsByFilter,
	} = useQuery({
		queryKey: [
			ERRORS_FILTER_LIST,
			{
				page,
				pageSize,
				sortDirection,
				sortFieldName,
				searchValue,
			},
		],
		queryFn: extractQueryParams(getErrorsByFilters),
		refetchOnWindowFocus: false,
	});

	const {
		mutate: handleUpdateErrorCode,
		isLoading: isLoadingUpdateErrorCode,
	} = useMutation({
		mutationKey: [ERROR_CODE_EDIT],
		mutationFn: editErrorCode,
		onSuccess: () => {
			if (closeModals) closeModals();

			dispatch(setSnackbar({
				message: responseMessages.updatedErrorCode,
				type: ESnackbarTypes.success,
			}));

			return [
				queryClient.invalidateQueries([ERRORS_FILTER_LIST]),
				queryClient.invalidateQueries([ERRORS_LIST]),
			];
		},
	});

	return {
		countOfRecords: data?.count || 0,
		handleUpdateErrorCode,
		errorsByFilter: data?.items || [],
		isLoadingErrorsByFilter: isLoadingErrorsByFilter || isFetchingErrorsByFilter,
		isLoadingUpdateErrorCode,
	};
};
